import { useCallback, useMemo, useState } from "react"

import { TextMatch } from "shared/text"
import { safeParseDate } from "shared/util"

import { TrialResult, TrialsPerson, useListTrial } from "interface/trial"

import { useSearch } from "features/search/context"
import { TrialYearStats } from "features/trial/types"

type useSisetusTrialPageProps = {
	sisetuCode: string
}
export const useSisetusTrialPage = ({
	sisetuCode
}: useSisetusTrialPageProps) => {
	const search = useSearch()
	
	const [searchWord, setSearchWord] = useState<string>('')
	const [selectedYear, setSelectedYear] = useState<number | null>(null)
	const [selectedPerson, setSelectedPerson] = useState<string | null>(null)

	const listState = useListTrial(
		search?.searchId,
		sisetuCode,
		{ updateTime: search?.updateTime }
	)

	const filterdTrialList = useMemo<TrialResult[]>(
		() => {
			if (!listState.data) return []
			if (searchWord !== '') {
				const match = new TextMatch(searchWord)
				return listState.data.filter(record => match.match(
  				record.site,
  				record.siteKey,
  				record.title,
  				record.publicTitle,
  
  				record.trialType,
  				record.clinicalTrialsType,
  				record.sponsor,
  				record.countries,
  
				  record.disease,
  				record.drugBrandNames,
  				record.drugGenericNames,
					record.age.lower,
					record.age.upper,
  				record.gender,

				  record.statusText,
  				record.phase,
  				record.progress,

  				record.studyPurpose,
  				record.interventionDescription,
  				record.includeCriteria,
  				record.excludeCriteria,
  				record.primaryOutcome,
  				record.secondaryOutcome,
  				record.designName
				))
			} else {
				return listState.data
			}
		},
		[listState.data, searchWord]
	)

	const isFiltered = useCallback(
		(trial: TrialResult, condition?: {
			selectedYear?: number | null,
			selectedPerson?: string | null
		}) => {
			if (condition?.selectedYear) {
				const year = safeParseDate(trial.registrationDate)?.getFullYear()
				if (year !== condition.selectedYear) return true
			}
			if (condition?.selectedPerson) {
				if (!trial.persons) return true
				if (!trial.persons.some(person => person.kojinCode === condition.selectedPerson)) return true
			}
			return false
		},
		[]
	)

	const yearsStats = useMemo<TrialYearStats>(
		() => {
			const list: { year: number, count: number }[] = []
			let startYear: number | null = null
			let lastYear: number | null = null
			let maxCount = 0
			filterdTrialList
				.filter(trial => isFiltered(trial, { selectedPerson }) === false)
				.forEach(trial => {
					const year = safeParseDate(trial.registrationDate)?.getFullYear()
					if (!year) return

					if (!startYear || startYear > year) startYear = year
					if (!lastYear || lastYear < year) lastYear = year

					const exist = list.find(exist => exist.year === year)
					if (exist) {
						exist.count++
						maxCount = Math.max(maxCount, exist.count)
					} else {
						list.push({ year, count: 1 })
						maxCount = Math.max(maxCount, 1)
					}
				})
			list.sort((a, b) => b.year - a.year)

			const years: { year: number, count: number }[] = []
			if (startYear && lastYear) {
				for(let year = startYear; year <= lastYear; year++) {
					const count = list.find(r => r.year === year)?.count ?? 0
					years.push({ year, count })
				}
			}
			return {
				list: years,
				maxCount
			}
		},
		[filterdTrialList, selectedPerson, isFiltered]
	)
	const trials = useMemo<TrialResult[]>(
		() => {
			return filterdTrialList.filter(
				trial => isFiltered(trial, { selectedYear, selectedPerson }) === false
			)
		},
		[filterdTrialList, isFiltered, selectedYear, selectedPerson]
	)
	const persons = useMemo<TrialsPerson[]>(
		() => {
			const personMap: {
				[kojinCode: string]: TrialsPerson & { count: number }
			} = {}
			filterdTrialList
				.filter(trial => isFiltered(trial, { selectedYear }) === false)
				.forEach(trial => trial.persons?.forEach(person => {
					if (person.sisetuCode !== sisetuCode) return
					const key = person.kojinCode ?? person.personName
					const existAuthor = personMap[key]
					if (!existAuthor) {
						personMap[key] = {
							...person,
							count: 1
						}
					} else {
						existAuthor.count = existAuthor.count + 1
					}
				}))
			const persons = Object.values(personMap)
			persons.sort((a, b) => b.count - a.count)
			return persons
		},
		[filterdTrialList, isFiltered, sisetuCode, selectedYear]
	)
	return {
		searchWord, setSearchWord,
		selectedYear, setSelectedYear,
		selectedPerson, setSelectedPerson,
		loading: listState.loading,
		error: listState.error,
		trials, yearsStats, persons
	}
}